<template>
  <BaseModal
    :name="USER_PROFILE_COMPLETE_MODAL_NAME"
    width-class="tw-max-w-2xl"
  >
    <div
      class="tw-relative tw-flex tw-w-full tw-flex-col tw-items-center tw-p-8"
    >
      <div class="tw-relative">
        <Avatar
          :src="currentUser?.avatarUrl"
          size="2xl"
          class="tw-absolute tw-left-1/2 tw-top-1/2 tw--translate-x-1/2 tw--translate-y-1/2"
        />
        <img :src="DealsCodeBackground" alt="" class="tw-w-96" />
      </div>

      <div class="tw-heading-1 tw-mt-16">Profile Complete</div>
      <div
        class="tw-mt-4 tw-w-full tw-max-w-sm tw-text-center tw-text-sm tw-text-neutral-500"
      >
        <p>
          Your GrowthMentor profile looks amazing! You're all set to start your
          growth journey.
        </p>
      </div>

      <div
        class="tw-mt-8 tw-w-full tw-rounded-xl tw-border tw-border-secondary-100 tw-p-6 tw-text-center"
        style="
          background: radial-gradient(
            65.59% 100% at 50% 0%,
            #e6d8ff 0%,
            #ffffff 100%
          );
        "
      >
        <div class="tw-text-lg tw-font-semibold tw-text-secondary-800">
          Share GrowthMentor with your network
        </div>
        <p class="tw-mt-1 tw-text-sm tw-text-secondary-800">
          Know people who could benefit from mentorship? Share your link -
          they'll get a free session, and you'll earn
          {{ currentUser.referral.commission }}% commission if they subscribe.
        </p>
        <FormInput
          type="text"
          readonly
          :model-value="inviteLink"
          class="tw-mt-4"
        >
          <template #input-append>
            <BaseButton
              @click="copyReferralLink"
              color="outline-light-purple"
              class="tw-shrink-0 tw-rounded-lg tw-py-2.5"
            >
              <BaseIcon
                name="ungroup"
                class="tw-mr-2 tw-hidden tw-h-4 tw-w-4 sm:tw-block"
              />
              Copy
            </BaseButton>
          </template>
        </FormInput>
      </div>

      <div
        class="tw-mt-8 tw-grid tw-w-full tw-gap-x-4 tw-gap-y-2 sm:tw-grid-cols-2"
      >
        <template v-if="hasRole(ROLE.MENTEE)">
          <BaseButton
            @click="closeModal(USER_PROFILE_COMPLETE_MODAL_NAME)"
            href="/search"
            is-inertia-link
            color="primary"
          >
            Browse Mentors
          </BaseButton>
          <BaseButton
            @click="closeModal(USER_PROFILE_COMPLETE_MODAL_NAME)"
            href="/opportunities/new"
            is-inertia-link
            color="outline-gray"
          >
            Post a Help Request
          </BaseButton>
        </template>
        <template v-if="hasRole(ROLE.MENTOR)">
          <BaseButton
            @click="closeModal(USER_PROFILE_COMPLETE_MODAL_NAME)"
            href="/opportunities"
            is-inertia-link
            color="primary"
            class="col-span-2"
          >
            Browse Help Requests
          </BaseButton>
        </template>
      </div>

      <IconButton
        class="tw-absolute tw-right-4 tw-top-4"
        @click="closeModal(USER_PROFILE_COMPLETE_MODAL_NAME)"
      >
        <CloseIcon />
      </IconButton>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import BaseModal from '@/shared/components/BaseModal.vue'
import { useActionCable } from '@/shared/composables/useActionCable'
import { useModals } from '@/shared/composables/useModals'
import { useDebugger } from '@/shared/composables/useDebugger'
import { USER_PROFILE_COMPLETE_MODAL_NAME } from '@/shared/data/globalVariables.js'
import { PROFILE_CHANNEL } from '@/shared/data/actionCableChannels.js'
import DealsCodeBackground from '@/assets/images/deals-code-background.png'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import { ROLE } from '@/shared/data/permissionSystem'
import FormInput from '@/shared/components/forms/FormInput.vue'
import BaseIcon from '@/shared/components/BaseIcon.vue'
import { useClipboard } from '@vueuse/core'
import { notify } from '@kyvg/vue3-notification'
import IconButton from '@/shared/components/IconButton.vue'
import CloseIcon from '@/assets/images/icons/close.svg'

const { currentUser } = useCurrentUser()
const { hasRole } = usePermissionSystem()
const { openModal, closeModal } = useModals()
const { $debug } = useDebugger()

const { subscribeChannel } = useActionCable({
  channelName: PROFILE_CHANNEL,
  onConnected: () => {
    $debug(`[AC Channel: ${PROFILE_CHANNEL}] connected`)
  },
  onReceived: (data: { event: string }) => {
    $debug(
      `[AC Channel: ${PROFILE_CHANNEL}] received data: ${JSON.stringify(data)}`,
    )
    if (data.event === 'profile_completed') {
      $debug(
        `[AC Channel: ${PROFILE_CHANNEL}] Profile completed, opening modal`,
      )
      openModal(USER_PROFILE_COMPLETE_MODAL_NAME)
    }
  },
  onDisconnected: () => {
    $debug(`[AC Channel: ${PROFILE_CHANNEL}] disconnected`)
  },
})

onMounted(() => {
  subscribeChannel()
})

const { copy } = useClipboard()

const inviteLink = computed(() => {
  return `${currentUser.value.referral.link}?ref=${currentUser.value.referral.code}`
})

const copyReferralLink = () => {
  copy(inviteLink.value)
  notify({
    type: 'success',
    text: 'Successfully copied invite link to clipboard!',
  })
}
</script>
